<template>
  <b-card class="border">
    <h6 class="text-primary font-weight-bold mb-0">
      Documentos disponibles ({{ loading ? '...' : files.length }})
    </h6>

    <!-- Seleccionar archivos para autorizar/rechazar -->
    <b-skeleton-wrapper :loading="loading">
      <!-- Loader -->
      <template #loading>
        <div
          v-for="cont in 10"
          :key="cont"
          :class="{ 'mt-2': cont === 1 }"
        >
          <b-skeleton
            :width="`${Math.floor(Math.random() * (100 - 25 + 1)) + 25}%`"
            height="1.5em"
            class="mb-50"
          />
        </div>
      </template>

      <!-- Lista de checkbox de arcivos -->
      <div class="mt-2">
        <!-- Seleccionar todos -->
        <div
          v-if="files.length"
          style="border-bottom: 1px solid #ebe9f1; padding-bottom: .5rem; margin-bottom: .5rem;"
        >
          <b-form-checkbox
            :checked="selectAllDocumentCheckbox"
            :indeterminate="isSelectAllDocumentCheckboxIndeterminate"
            :disabled="loadingPost || loading"
            @change="selectAllCheckboxUpdate"
          >
            <b>Seleccionar todos</b>
          </b-form-checkbox>
        </div>
        <div
          v-else
          class="text-center"
        >
          <p>Este aspirante no cuenta con documentos pendientes. ¿Qué tal si revisamos otro aspirante?</p>
        </div>

        <!-- Lista de tipos de documentos cargados -->
        <b-form-checkbox
          v-for="option in files"
          :key="option.id"
          v-model="selectedFiles"
          :value="option.id"
          name="flavour-3a"
          class="mb-50"
          :disabled="loadingPost || loading"
        >
          {{ option.tipoNombre }}
        </b-form-checkbox>

        <transition name="fade">
          <section
            v-if="selectedFiles.length"
            class="text-center mt-2"
          >
            <hr>
            <!-- Qué hacer con los seleccionados -->
            <p>
              ¿Qué desea hacer con los <b>elementos seleccionados</b>?
            </p>
            <div>
              <b-form-radio-group
                v-model="willAuthorize"
                :options="[{ text: 'Autorizar', value: 'AUT' },{ text: 'Rechazar', value: 'CAN' }]"
                class="mb-2"
                :disabled="loadingPost || loading"
              />
            </div>

            <!-- ¿Enviar correo? -->
            <transition name="fade">
              <div
                v-if="willAuthorize === 'CAN'"
                class="d-flex align-items-center justify-content-center mb-2"
              >
                <span class="mr-1 mb-0">
                  ¿Enviar correo al aspirante? - <b>{{ willNotify === 'Y' ? 'Sí, enviar correo' : 'No, solo cancelar' }}</b>
                </span>
                <b-form-checkbox
                  v-model="willNotify"
                  value="Y"
                  unchecked-value="N"
                  switch
                >
                  <span class="switch-icon-left">
                    <feather-icon icon="BellIcon" />
                  </span>
                  <span class="switch-icon-right">
                    <feather-icon icon="BellOffIcon" />
                  </span>
                </b-form-checkbox>
              </div>
            </transition>

            <!-- Mensaje de cancelación -->
            <transition name="fade">
              <b-form-textarea
                v-if="willAuthorize === 'CAN'"
                v-model="message"
                placeholder="Ingrese motivo por el cual está rechazando los documentos"
                rows="5"
                class="mb-2"
                :disabled="loadingPost || loading"
              />
            </transition>

            <!-- ¿Que hará cada acción? -->
            <b-alert
              show
              :variant="willAuthorize === 'CAN' ? 'danger' : 'primary'"
              class="text-left"
            >
              <h4 class="alert-heading">
                La opción "{{ willAuthorize === 'CAN' ? 'Rechazar' : 'Autorizar' }}", hará lo siguiente:
              </h4>
              <div class="alert-body text-left">
                <ul v-if="willAuthorize === 'CAN'">
                  <li><b>Rechazará</b> los documentos seleccionados.</li>
                  <li>
                    <span v-if="willNotify === 'Y'"><b>Enviará</b> correo al aspirante indicando el motivo de rechazo.</span>
                    <span v-else>Ha indicado que <b>no desea enviar correo al aspirante</b>.</span>
                  </li>
                  <li v-if="willNotify === 'Y'">
                    <b>Se listarán</b> los documentos seleccionados como <b>"rechazados"</b> en el correo, así mismo se agregará acceso al módulo de documentación del aspirante para que pueda volver a subirlos.
                  </li>
                </ul>
                <ul v-else>
                  <li><b>Autorizará</b> los documentos seleccionados.</li>
                  <li><b>No</b> enviará correo al aspirante.</li>
                </ul>
              </div>
            </b-alert>

            <!-- Botón -->
            <transition name="fade">
              <b-button
                v-if="willAuthorize"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                :disabled="loadingPost || loading"
                @click="makePost"
              >
                <b-spinner
                  v-if="loadingPost"
                  small
                  class="mr-50"
                />
                {{ loadingPost ? 'Conectando con el servidor ...' : willAuthorize === 'AUT' ? 'Autorizar documentos' : 'Solicitar nuevamente' }}
              </b-button>
            </transition>
          </section>
        </transition>
      </div>
    </b-skeleton-wrapper>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BAlert,
  BSkeletonWrapper,
  BSkeleton,
  BFormTextarea,
  BFormCheckbox,
  BFormRadioGroup,
  BSpinner,
} from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formhttp } from '@core/utils/utils'
import { authorizeOrRefuseDocument } from '@/views/components/documentacion/useDocumentacion'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'

export default {
  components: {
    BCard,
    BButton,
    BAlert,
    BSkeletonWrapper,
    BSkeleton,
    BFormTextarea,
    BFormCheckbox,
    BFormRadioGroup,
    BSpinner,
  },
  directives: {
    'height-fade': heightFade,
    Ripple,
  },
  props: {
    files: {
      type: Array,
      default: () => [],
      required: true,
    },
    user: {
      type: [Number, String, null],
      default: null,
      required: true,
    },
    plan: {
      type: [Number, null],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const willAuthorize = ref(null)
    const willNotify = ref('N')
    const loadingPost = ref(false)
    const message = ref('')
    const selectedFiles = ref([])

    // * Acciones para seleccionar/deseleccionar todos [Checkbox]
    const selectAllDocumentCheckbox = computed(() => props.files.length && (props.files.length === selectedFiles.value.length))
    const isSelectAllDocumentCheckboxIndeterminate = computed(() => Boolean(selectedFiles.value.length) && props.files.length !== selectedFiles.value.length)
    const selectAllCheckboxUpdate = val => { selectedFiles.value = val ? props.files.map(item => item.id) : [] }

    // * Make post request
    const makePost = () => {
      loadingPost.value = true
      const data = { f: JSON.stringify(selectedFiles.value), p: props.user, x: willAuthorize.value }
      if (willAuthorize.value === 'CAN') {
        data.data = message.value
        data.not = willNotify.value
        if (props.plan) data.pl = props.plan
      }

      authorizeOrRefuseDocument(data)
        .then(result => {
          loadingPost.value = false
          const { icon, label } = formhttp(result.status)

          if (result.status === 200) {
            selectedFiles.value = []
            message.value = ''
            willAuthorize.value = null
            willNotify.value = 'N'
            emit('request-ended')
          }

          toast({
            component: ToastificationContent,
            props: {
              title: 'Resultado de solicitud',
              text: result.message,
              icon,
              variant: label,
            },
          },
          { timeout: 10000 })
        }, error => {
          loadingPost.value = false
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error solicitud',
              text: error,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          },
          { timeout: 10000 })
        })
    }

    return {
      willAuthorize,
      willNotify,
      selectedFiles,
      message,
      selectAllDocumentCheckbox,
      isSelectAllDocumentCheckboxIndeterminate,
      selectAllCheckboxUpdate,

      // Functions
      makePost,

      // Loader
      loadingPost,
    }
  },
}
</script>
